// src/api/reportsApi.ts

import api from './axios';

export type ReportSection =
  | "Executive Snapshot"
  | "Cash Flow Capacity (Trailing Window)"
  | "Liquidity & Leverage (Snapshot)"
  | "Profitability & Cost Structure (Trailing Window)"
  | "Growth & Volatility"
  | "Cash Health & Runway"
  | "Forward-Looking Projections"
  | "Working Capital & Conversion"
  | "Concentration & Recurrence"
  | "Credit Box / Approval Criteria"
  | "Borrower Profile & Management Assessment"
  | "Business Metrics (P&L & Balance Sheet)"
  | "Conclusion & Recommendation";

export type WindowOption = 3 | 6 | 9;

const reportsApi = {
  generateReport: async (
    businessSlug: string,
    window: WindowOption,
    sections: ReportSection[]
  ): Promise<{ blob: Blob; filename: string }> => {
    const url = `/reports/${businessSlug}/detailed-report`;
    let res;
    try {
      res = await api.get(url, {
        params: { window, sections, _t: Date.now() }, // Cache breaking
        paramsSerializer: params => {
          const usp = new URLSearchParams();
          usp.set('window', String(params.window));
          if (params._t) usp.set('_t', String(params._t));
          (params.sections as string[]).forEach(s => usp.append('sections', s));
          return usp.toString();
        },
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      });
      console.log('Report API Response Headers:', res.headers); // Debug log
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        // If the error response is a blob (likely JSON error), read it
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          throw new Error(json.detail || error.message);
        } catch (e: any) {
          // If parsing fails, throw original or new error
          // e might be the error from JSON.parse, so use original text if possible?
          // Fallback to error.message if parsing fails
          throw new Error(error.response?.statusText || error.message);
        }
      }
      throw error;
    }

    // Check if the response is actually a JSON error (despite responseType: 'blob')
    const contentType = res.headers['content-type'];
    if (contentType && contentType.includes('application/json')) {
      // Convert blob back to text to read the error
      const text = await (res.data as Blob).text();
      try {
        const json = JSON.parse(text);
        throw new Error(json.detail || 'Report generation failed');
      } catch (e: any) {
        throw new Error(e.message || 'Report generation failed (Unknown Error)');
      }
    }

    // NEW: Check if response is HTML (likely a redirect to login page)
    if (contentType && (contentType.includes('text/html') || contentType.includes('text/plain'))) {
      throw new Error("Failed to generate report: Server returned HTML/Text (Session might be expired).");
    }

    // Check size - if it's too small, might be an error
    // Strict Check: Validate Magic Bytes for PDF
    // Read the first 5 bytes to check for %PDF-
    const headerSlice = res.data.slice(0, 5);
    const headerText = await headerSlice.text();

    if (headerText !== '%PDF-') {
      // It's not a PDF. Likely HTML or error text.
      const debugSlice = res.data.slice(0, 500);
      const debugText = await debugSlice.text();
      console.error("Invalid PDF Header received:", headerText);
      console.error("Content Preview:", debugText);

      if (debugText.includes("<!DOCTYPE html>") || debugText.includes("<html")) {
        throw new Error("Failed to generate report: Server returned HTML (likely login page or error page). Please refresh or log in again.");
      }

      throw new Error(`Invalid file format received (Header: ${headerText}). The server response was not a valid PDF.`);
    }

    // If we passed the check, it's valid.
    console.log("Valid PDF received. Type:", res.data.type, "Size:", res.data.size);

    // Extract filename from header if possible
    const disposition = res.headers['content-disposition'];
    let filename = `${businessSlug}_${window}m_detailed_report.pdf`;

    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        const extracted = matches[1].replace(/['"]/g, '');
        if (extracted && extracted.trim().length > 0) {
          filename = extracted;
        }
      }
    }

    // Fallback: Ensure it ends in .pdf
    if (!filename.toLowerCase().endsWith('.pdf')) {
      filename += '.pdf';
    }

    console.log('Final Filename:', filename);

    return {
      blob: new Blob([res.data], { type: 'application/pdf' }),
      filename
    };
  },
};

export default reportsApi;
